import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';

/**
 * Check if an organization with the given name and domain already exists
 */
export function useCheckOrganizationExists() {
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const checkExists = async (name: string, domain: string): Promise<boolean> => {
    if (!name || !domain) return false;

    setIsChecking(true);
    try {
      // Query without custom_domain since it doesn't exist in schema
      const { data, error } = await supabase
        .from('organizations')
        .select('id')
        .eq('name', name)
        .limit(1);

      if (error) {
        console.error('Check duplicate error:', error);
        return false;
      }

      const exists = data && data.length > 0;
      setIsDuplicate(exists);
      return exists;
    } finally {
      setIsChecking(false);
    }
  };

  return { checkExists, isDuplicate, isChecking };
}

/**
 * Get list of all organizations for platform admin
 */
export function useOrganizationList() {
  return useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, custom_domain, plan, created_at, created_by_user_id')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });
}

/**
 * Get organization by ID with full details
 */
export function useOrganization(organizationId: string | null) {
  return useQuery({
    queryKey: ['organization', organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', organizationId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!organizationId,
  });
}

/**
 * Get user's organizations
 */
export function useUserOrganizations(userId: string | null) {
  return useQuery({
    queryKey: ['user-organizations', userId],
    queryFn: async () => {
      if (!userId) return [];

      const { data, error } = await supabase
        .from('organization_members')
        .select('organization_id, organizations!inner(id, name, custom_domain, plan)')
        .eq('user_id', userId);

      if (error) throw error;
      return data?.map(d => d.organizations) || [];
    },
    enabled: !!userId,
  });
}

/**
 * Extract domain from email address
 * @example extractDomainFromEmail('user@company.com') => 'company.com'
 */
export function extractDomainFromEmail(email: string): string {
  const match = email.match(/@(.*)$/);
  return match ? match[1].toLowerCase() : '';
}

/**
 * Validate domain format
 * @example isValidDomain('company.com') => true
 * @example isValidDomain('invalid') => false
 */
export function isValidDomain(domain: string): boolean {
  // Basic domain validation
  const domainRegex = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i;
  return domainRegex.test(domain);
}
